import React, { useState, useRef, useEffect } from 'react';
import { PauseCircle, PlayCircle } from 'lucide-react';
import api from '../services/api';
import { useNotificationContext } from '../contexts/NotificationContext';
import { MOTIVOS_PAUSA, puedePausarSla, slaPausadoManual } from '../utils/slaPausa';

// Pausa manual del SLA (staff/admin). Es para el ticket que espera a un tercero (proveedor, desarrollo, otra área) y NO al usuario:
// sigue en su estado, pero el reloj se detiene hasta «Reanudar SLA». Que el usuario responda no lo reanuda.
export default function TicketPausaSla({ ticket, onCambio }) {
  const { alertSuccess, alertError } = useNotificationContext();
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [abierto]);

  const pausar = async () => {
    setEnviando(true);
    try {
      await api.post(`/tickets/${ticket.id}/pausar-sla`, { motivo: motivo.trim() });
      alertSuccess('SLA en pausa. Se reanuda cuando lo indiques.');
      setAbierto(false);
      setMotivo('');
      onCambio?.();
    } catch (err) {
      alertError(err.response?.data?.message || 'No se pudo pausar el SLA.');
    } finally {
      setEnviando(false);
    }
  };

  const reanudar = async () => {
    setEnviando(true);
    try {
      await api.post(`/tickets/${ticket.id}/reanudar-sla`);
      alertSuccess('SLA reanudado.');
      onCambio?.();
    } catch (err) {
      alertError(err.response?.data?.message || 'No se pudo reanudar el SLA.');
    } finally {
      setEnviando(false);
    }
  };

  if (slaPausadoManual(ticket)) {
    const desde = new Date(ticket.fechaEsperandoUsuario).toLocaleString('es-CL', { timeZone: 'America/Santiago', dateStyle: 'short', timeStyle: 'short' });
    return (
      <button
        onClick={reanudar}
        disabled={enviando}
        className="flex items-center gap-1.5 px-4 py-2 border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors font-medium text-sm disabled:opacity-60"
        title={`El SLA está en pausa desde ${desde}. Pulsa para reanudarlo.`}
      >
        <PlayCircle className="w-4 h-4" />
        {enviando ? 'Reanudando…' : 'Reanudar SLA'}
      </button>
    );
  }

  if (!puedePausarSla(ticket)) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setAbierto((v) => !v)}
        className="flex items-center gap-1.5 px-4 py-2 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors font-medium text-sm"
        title="Detener el reloj del SLA mientras el ticket espera a un tercero"
      >
        <PauseCircle className="w-4 h-4" />
        Pausar SLA
      </button>
      {abierto && (
        <div className="absolute right-0 mt-2 w-80 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            El tiempo en pausa no cuenta para el SLA. El ticket sigue en «{ticket.estado}» y queda anotado el motivo.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {MOTIVOS_PAUSA.map((m) => (
              <button key={m} type="button" onClick={() => setMotivo(m)}
                className="px-2 py-1 text-xs rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                {m}
              </button>
            ))}
          </div>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Motivo de la pausa (obligatorio)"
            className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAbierto(false)} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
            <button onClick={pausar} disabled={enviando || motivo.trim().length < 3} className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60">
              {enviando ? 'Pausando…' : 'Pausar SLA'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
